import React, { useState, useMemo } from "react";
import {
  AssignTable,
  Button,
  Chip,
  CopyToClipboard,
  FormFieldSet,
  NoData,
  Modal,
  showToast,
  LogoLoader,
  TableAdv,
} from "../../../../../components";
import { mapTableRows } from "../../../../../utils";
import { useDispatch, useSelector } from "react-redux";
import {
  getFollowAssignDataStart,
  successCreateInstantlyCampaign,
} from "../../../../../redux/slices/compaigns/compaignsSlice";
import { useEffect } from "react";
import dayjs from "dayjs";
import Assign from "../Assign";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { fetchRaDownloadSummaryReport } from "../../../../../redux/slices/reports/reportsApi";
import { getEndOfDay, getStartOfDay } from "../../../../../utils/common";
import { formatWords2 } from "../../../../../utils/stringUtils";

// Dummy generators for Latest Data view
const generateBdmList = (count = 6) => {
  return Array.from({ length: count }, (_, i) => ({
    id: i + 1,
    name: `BDM ${i + 1}`,
    email: `bdm${i + 1}@example.com`,
  }));
};

const generateTeamList = (count = 9) => {
  const designations = ["senior", "junior", "fresher"];
  return Array.from({ length: count }, (_, i) => ({
    id: i + 1,
    name: `Team ${i + 1}`,
    email: `team${i + 1}@example.com`,
    designation: designations[Math.floor(Math.random() * designations.length)],
    emailsList: Array.from(
      { length: Math.floor(Math.random() * 4) + 2 },
      (_, j) => `team${i + 1}_${j + 1}@example.com`,
    ),
  }));
};

const Leads = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const createCampaigns =
    useSelector((state) => state.compaigns?.instantly?.createCampaigns) || {};
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const before10Days = new Date();
    before10Days.setDate(today.getDate() - 1);

    return {
      from: new Date(),
      to: new Date(),
    };
  });
  const [leadsSelectedValues, setLeadsSelectedValues] = useState();
  const [assignmentOutput, setAssignmentOutput] = useState(null);
  const [isExcelData, setIsExcelData] = useState(true);
  const [followupDate, setFollowupDate] = useState();
  const [emailIds, setEmailIDs] = useState();
  const [isEmailData, setIsEmailData] = useState(true);

  const [emailInput, setEmailInput] = useState("");
  const [typedEmails, setTypedEmails] = useState([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [invalidEmails, setInvalidEmails] = useState([]);

  const [googleSheetData, setGoogleSheetData] = useState([]);
  const [googleSheetColumns, setGoogleSheetColumns] = useState([]);
  const [googleSheetUrl, setGoogleSheetUrl] = useState("");
  const [isSheetLoading, setIsSheetLoading] = useState(false);
  const [sheetError, setSheetError] = useState(""); // for errors
  const sampleData = {
    bdmList: generateBdmList(7),
    teamList: generateTeamList(20),
  }; // Latest Data (dummy)

  const fetchGoogleSheetData = async () => {
    if (!googleSheetUrl) {
      setSheetError("Please enter a Google Sheet URL");
      return;
    }

    try {
      setIsSheetLoading(true);
      setSheetError("");

      const match = googleSheetUrl.match(/\/d\/([a-zA-Z0-9-_]+)/);
      if (!match || !match[1]) throw new Error("Invalid Google Sheet URL");
      const sheetId = match[1];

      const csvUrl = `https://docs.google.com/spreadsheets/d/${sheetId}/export?format=csv`;
      const response = await fetch(csvUrl);
      if (!response.ok) throw new Error("Failed to fetch Google Sheet");

      const csvText = await response.text();
      const { data, meta } = Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        preview: 10,
      });

      const columns = meta.fields.map((col) => {
        let defaultType = "";
        const lowerCol = col.toLowerCase();
        if (lowerCol.includes("email")) defaultType = "email";
        else if (lowerCol.includes("first")) defaultType = "firstName";
        else if (lowerCol.includes("last")) defaultType = "lastName";
        else if (lowerCol.includes("company")) defaultType = "companyName";
        else if (lowerCol.includes("title") || lowerCol.includes("job"))
          defaultType = "jobTitle";
        else if (lowerCol.includes("phone")) defaultType = "phone";
        else if (lowerCol.includes("website")) defaultType = "website";

        return {
          columnName: col,
          selectedType: defaultType,
          samples: data
            .map((row) => row[col])
            .filter(Boolean)
            .slice(0, 5),
        };
      });

      setGoogleSheetColumns(columns);
    } catch (err) {
      console.error(err);
      setSheetError("Failed to import Google Sheet data");
    } finally {
      setIsSheetLoading(false);
    }
  };

  const handleEmailKeyPress = (e) => {
    if (e.key === "Enter" && emailInput?.trim() !== "") {
      e.preventDefault();
      const email = emailInput?.trim();
      if (isValidGmail(email)) {
        setInvalidEmails([email]); // show error
        return;
      }
      const domain = email?.split("@")[1]?.split(".")[0] || "";
      const provider =
        domain?.charAt(0)?.toUpperCase() + domain?.slice(1)?.toLowerCase();
      setTypedEmails((prev) => [
        ...prev,
        { email, emailProvider: provider, status: "Not yet Contacted" },
      ]);

      setEmailInput(""); // clear input
      setInvalidEmails([]); // clear error
    }
  };
  const handleEmailTagsChange = (emails) => {
    const emailsArray = Array.isArray(emails) ? emails : [emails];
    const validEmails = [];
    const invalidEmailsList = [];
    emailsArray.forEach((email) => {
      const trimmedEmail = email?.trim();
      if (!trimmedEmail) return;
      if (isValidGmail(trimmedEmail)) {
        invalidEmailsList.push(trimmedEmail);
      } else {
        const domain = trimmedEmail.split("@")[1]?.split(".")[0] || "";
        const provider =
          domain.charAt(0).toUpperCase() + domain.slice(1).toLowerCase();

        validEmails.push({
          email: trimmedEmail,
          emailProvider: provider,
          status: "Not yet Contacted",
        });
      }
    });
    if (invalidEmailsList.length > 0) {
      setInvalidEmails(invalidEmailsList);
      return;
    }
    const newEmails = validEmails.filter(
      (newEmail) =>
        !typedEmails.some(
          (existing) =>
            existing.email.toLowerCase() === newEmail.email.toLowerCase(),
        ),
    );
    setTypedEmails((prev) => [...prev, ...newEmails]);
    setInvalidEmails([]);
    setEmailInput([]); // clear input if needed
  };

  const isValidGmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // basic email validation
    return regex.test(email.trim()) && email.trim().endsWith("@gmail.com");
  };
  // Add this function inside your Leads component
  // const downloadSampleData = (type) => {
  //   const sampleEmails = [
  //     "alex.morgan123@gmail.com",
  //     "priya.sharma.test@gmail.com",
  //     "rahul.verma1988@gmail.com",
  //     "sarah.patel01@gmail.com",
  //   ];

  //   if (type === "csv") {
  //     const csvContent = "email\n" + sampleEmails.join("\n");
  //     const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  //     const url = URL.createObjectURL(blob);
  //     const link = document.createElement("a");
  //     link.href = url;
  //     link.setAttribute("download", "sample_emails.csv");
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);
  //   } else if (type === "excel") {
  //     const ws = XLSX.utils.json_to_sheet(
  //       sampleEmails.map((email) => ({ email }))
  //     );
  //     const wb = XLSX.utils.book_new();
  //     XLSX.utils.book_append_sheet(wb, ws, "Emails");
  //     XLSX.writeFile(wb, "sample_emails.xlsx");
  //   }
  // };
  const downloadSampleData = (type) => {
    const sampleData = [
      {
        email: "alex.morgan123@gmail.com",
        website: "www.alexmorgan.com",
        first_name: "Alex",
        last_name: "Morgan",
        company_name: "TechWave Solutions",
        phone: "+1 555-123-4567",
      },
      {
        email: "priya.sharma.test@gmail.com",
        website: "www.priyasharma.in",
        first_name: "Priya",
        last_name: "Sharma",
        company_name: "NextGen IT",
        phone: "+91 98765-43210",
      },
      {
        email: "rahul.verma1988@gmail.com",
        website: "www.rahulverma.com",
        first_name: "Rahul",
        last_name: "Verma",
        company_name: "Global Softwares",
        phone: "+91 99999-11111",
      },
      {
        email: "sarah.patel01@gmail.com",
        website: "www.sarahpatel.io",
        first_name: "Sarah",
        last_name: "Patel",
        company_name: "BrightEdge Inc",
        phone: "+1 555-789-6543",
      },
    ];

    if (type === "csv") {
      const headers = [
        "email",
        "website",
        "first_name",
        "last_name",
        "company_name",
        "phone",
      ];
      const csvRows = [
        headers.join(","), // header row
        ...sampleData.map((item) =>
          headers.map((header) => `"${item[header] || ""}"`).join(","),
        ),
      ];
      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "sample_contacts.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (type === "excel") {
      const ws = XLSX.utils.json_to_sheet(sampleData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Contacts");
      XLSX.writeFile(wb, "sample_contacts.xlsx");
    }
  };

  // Excel Upload Table Columns
  const excelColumns = [
    {
      key: "email",
      label: "Email",
      width: "min-w-[200px]! max-w-[250px]!",
      render: (row) => (
        <div className="flex items-center">
          <CopyToClipboard content={row.email} title={row.email} />
          {row.email}
        </div>
      ),
    },
    {
      key: "emailProvider",
      label: "Email Provider",
      width: "min-w-[200px]! max-w-[250px]!",
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <Chip label={row.status} className="!bg-gray-300 py-2 px-4" />
      ),
    },
  ];

  // Manual Email Table Columns
  const emailColumns = [
    {
      key: "email",
      label: "Email",
      width: "min-w-[200px]! max-w-[250px]!",
      render: (row) => (
        <div className="flex items-center">
          <CopyToClipboard content={row.email} title={row.email} />
          {row.email}
        </div>
      ),
    },
    {
      key: "emailProvider",
      label: "Email Provider",
      width: "min-w-[200px]! max-w-[250px]!",
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <Chip label={row.status} className="!bg-gray-300 py-2 px-4" />
      ),
    },
    {
      key: "actions",
      label: "Actions",

      render: (row, index) => (
        <Button
          title="Delete"
          size="sm"
          color="red"
          // variant="text"
          onClick={() => {
            const updatedEmails = [...typedEmails];
            updatedEmails.splice(index, 1);
            setTypedEmails(updatedEmails);
          }}
        />
      ),
    },
  ];
  const internalEmailsColumns = [
    {
      key: "firstName",
      label: "First Name",
      width: "min-w-[200px]! max-w-[250px]!",
    },
    {
      key: "lastName",
      label: "Last Name",
      width: "min-w-[200px]! max-w-[250px]!",
    },
    {
      key: "email",
      label: "Email",
      width: "min-w-[200px]! max-w-[250px]!",
      render: (row) => (
        <div className="flex items-center">
          <CopyToClipboard content={row.email} title={row.email} />
          {row.email}
        </div>
      ),
    },
    {
      key: "companyName",
      label: "Company Name",
      width: "min-w-[200px]! max-w-[250px]!",
    },
    {
      key: "jobTitle",
      label: "Job Title",
      width: "min-w-[200px]! max-w-[250px]!",
    },
    {
      key: "jobLocation",
      label: "Job Location",
      width: "min-w-[200px]! max-w-[250px]!",
    },
  ];

  const typedEmailRows = useMemo(
    () => mapTableRows(typedEmails, emailColumns),
    [typedEmails],
  );

  const handleAssignmentOutput = (assignments) => {
    setAssignmentOutput(assignments);
  };

  // Dummy rows for Excel + Manual
  const excelRows = Array.from({ length: 6 }, () => ({
    email: "asthavsh17@tektree.in",
    emailProvider: "Google",
    status: "Not yet Contacted",
  }));

  const emailRows = Array.from({ length: 1 }, () => ({
    email: "asthavsh17@tektree.in",
    emailProvider: "Google",
    status: "Not yet Contacted",
  }));

  const excelMappedRows = mapTableRows(excelRows, excelColumns);
  const emailMappedRows = mapTableRows(emailRows, emailColumns);

  // --- FollowUp API Integration ---
  const fetchFollowAssignments = async (date) => {
    setDateRange(date);
    // dispatch(
    //   getFollowAssignDataStart({
    //     coll: "Shuffle",
    //     lim: 1,
    //     pg: 1,
    //     ref: "Jobwithcompany Propects",
    //     extret: "uiid firstName email lastName level",
    //     fil: JSON.stringify(filter),
    //     sort: JSON.stringify({ uiid: -1 }),
    //     extincl: JSON.stringify(["uiid"]),
    //   })
    // );
  const res = await fetchRaDownloadSummaryReport({
          coll: "Job",
          pg: 1,
          lim: 15000,
          fil: {
            addedBy: {
              type: "RA_SUMMARY_REPORT",
              value: [
                // dateRange.from,
                // dateRange.to
                getStartOfDay(date.from),
                getEndOfDay(date.to)
              ],
              userId: [], // no need [...uiids] if already array
              status: 1,
            },
          },
          useAggregate: true,
          ret: "uiid name website status jobs.uiid jobs.title jobs.status jobs.location jobs.addedOn jobs.addedBy jobs.description jobs.prospects.uiid jobs.prospects.firstName jobs.prospects.lastName jobs.prospects.mobile jobs.prospects.email jobs.prospects.inpersonResponseStatus",
          extret: "uiid firstName lastName",
        });
         const data = res?.data?.data || [];
              // console.log(data)
              // Flatten deeply nested jobs + prospects arrays
             const flattened = data.flatMap(company => {
  const jobs = company.jobs || [];

      return jobs.flatMap((job) => {
        const prospects =
          job.prospects && job.prospects.length > 0 ? job.prospects : [null];

        return prospects
          .map((prospect) => ({
            companyName: formatWords2(company?.name),
            website: company?.website,
            jobTitle: formatWords2(job?.title),
            jobLocation: `${job?.location?.city},${job?.location?.state}`,
            firstName: prospect?.firstName ?? "",
            lastName: prospect?.lastName ?? "",
            email: prospect?.email?.email ?? prospect?.email ?? "",
            phone: prospect?.mobile?.number ?? "",
          }))
          .filter((row) => row.email); // keeps non-null, non-undefined, non-empty string
      });
    });
    setTypedEmails(flattened);
  };

  const handleChangeOptions = (value) => {
    setLeadsSelectedValues(value);
    const todayDate = new Date();
    if (value === 2 || value === 3) {
      setFollowupDate(todayDate);
      fetchFollowAssignments({ from: new Date(), to: new Date() });
      if (value === 3) {
        todayDate.setDate(todayDate.getDate() - 1);
        setFollowupDate(todayDate);
        fetchFollowAssignments(todayDate);
      }
    }
  };

  const handleSave = () => {
    if (!leadsSelectedValues) {
      return showToast("Please select any one option", "error");
    }
    if (typedEmails.length === 0) {
      return showToast("plese add the emails", "error");
    }
    const action = {
      leadsType: leadsSelectedValues,
      leads: typedEmails,
    };
    dispatch(successCreateInstantlyCampaign(action));
    // navigate("/ats/campaigns/add-campaign#sequences");
    navigate("/ats/campaigns/add-campaign?add-campaign=sequences");
  };

  useEffect(() => {
    if (leadsSelectedValues === 1) {
      setShowUploadModal(true); // open modal immediately
    }
  }, [leadsSelectedValues]);
  useEffect(() => {
    if (createCampaigns && Object.keys(createCampaigns).length > 0) {
      // setCampaignName(createCampaigns?.name);
      setLeadsSelectedValues(createCampaigns?.leadsType);
      setTypedEmails(createCampaigns?.leads);
    }
  }, [createCampaigns]);
  return (
    <div className="mt-4">
      {/* <div className="grid grid-cols-4">
        <FormFieldSet
          type="select"
          placeholder="Select"
          options={[
            { label: "Upload Excel/csv sheet", value: 1 },
            { label: "Latest Data", value: 2 },
            { label: "Follow-up Data", value: 3 },
            { label: "Enter Emails Manually", value: 4 },
            { label: "Use Google Sheets", value: 5 },
          ]}
          value={leadsSelectedValues}
          onChange={(value) => handleChangeOptions(value)}
        />
      </div> */}
      <div className="flex gap-3 items-end">
        <div className="min-w-60">
          <FormFieldSet
            type="select"
            placeholder="Select"
            title="Add Lead Data"
            options={[
              { label: "Upload Excel/csv sheet", value: 1 },
              { label: "Latest Data", value: 2 },
              { label: "Follow-up Data", value: 3 },
              { label: "Enter Emails Manually", value: 4 },
              { label: "Use Google Sheets", value: 5 },
            ]}
            value={leadsSelectedValues}
            onChange={(value) => handleChangeOptions(value)}
          />
        </div>
        <Button title="Save" onClick={() => handleSave()} />{" "}
        {/*// mahee added 15-10-25 */}
      </div>

      {/* Excel Upload */}
      {leadsSelectedValues === 1 && (
        <div className="flex items-center my-10">
          {isExcelData ? (
            <TableAdv
              columns={emailColumns}
              rows={mapTableRows(typedEmails, emailColumns)}
              showCheckbox
              tableId={"RA_campaigns_leads"}
              toolbarRight={{
                search: true,
                fullScreen: true,
                filter: true,
                download: false,
                add: [
                  {
                    component: (
                      <div className="flex gap-3">
                        <Button
                          title="Add Lead"
                          size="sm"
                          icon={"add"}
                          className="mr-2"
                        />
                        {/* <Button
                          title="Upload"
                          size="sm"
                          onClick={() => setShowUploadModal(true)} // same state
                        /> */}
                      </div>
                    ),
                  },
                ],
              }}
            />
          ) : (
            <NoData />
          )}
        </div>
      )}

      {(leadsSelectedValues === 3 || leadsSelectedValues === 2) && (
        <div className="flex-1 inner-section p-4 rounded my-5">
          {leadsSelectedValues !== 2 && (
            <div className="grid grid-cols-5">
              {/* <FormFieldSet
                type="datepicker"
                title="Select Date"
                placeholder="Select"
                value={followupDate}
                maxDate={new Date()}
                onChange={(val) => {
                  setFollowupDate(val);
                  fetchFollowAssignments(val);
                }}
              /> */}
              <FormFieldSet
                noTitle
                placeholder="Select date range"
                type="datepickerRange"
                size="sm"
                value={dateRange}
                onChange={(range) => {
                  fetchFollowAssignments(range);
                }}
                shadow={true}
              />
            </div>
          )}

          {/* <div className="flex justify-center items-center my-10">
            {followupDate ? (
              transformedData.bdmList.length > 0 ? (
                <AssignTable
                  data={transformedData}
                  output={handleAssignmentOutput}
                  dragndrop={false}
                />
              ) : (
                <NoData />
              )
            ) : (
              <NoData />
            )}
          </div> */}
          <TableAdv
            totalCount={typedEmailRows.length}
            tableId={"RA_campaigns_leadsvalue3table"}
            columns={internalEmailsColumns}
            rows={typedEmailRows}
            toolbarRight={{
              fullScreen:true
            }}
          />
        </div>
      )}

      {/* Enter Emails Manually */}
      {leadsSelectedValues === 4 && (
        <div className="flex-1 inner-section p-4 rounded my-5">
          <div className="grid grid-cols-5">
            <FormFieldSet
              type="tags"
              title="Email ID"
              placeholder="Type email and press Enter"
              value={emailInput}
              onChange={(val) => handleEmailTagsChange(val)}
              // onKeyDown={handleEmailKeyPress}
              error={
                invalidEmails.length > 0
                  ? `Invalid Gmail: ${invalidEmails.join(", ")}`
                  : null
              }
            />
          </div>
          <div className="flex justify-center items-center my-10">
            {isEmailData ? (
              <TableAdv
                columns={emailColumns}
                tableId={"RA_campaigns_leadsvalue4table"}
                rows={typedEmailRows}
                showCheckbox
                hideToolBar
              />
            ) : (
              <NoData />
            )}
          </div>
        </div>
      )}

      {/* Google Sheets */}

      {leadsSelectedValues === 5 && (
        <div className="flex-1 inner-section p-6 rounded my-5">
          <div className="mb-6 flex flex-col items-center">
            <div className="w-3/4 max-w-2xl">
              <FormFieldSet
                type="text"
                title="Google Sheet URL"
                placeholder="Copy Google Sheet URL and paste here"
                value={googleSheetUrl}
                onChange={(val) => setGoogleSheetUrl(val)}
              />
            </div>
            <div className="mt-4">
              <Button
                title="Import Emails"
                size="sm"
                onClick={fetchGoogleSheetData}
                disabled={isSheetLoading}
              />
            </div>

            {/* Show loader while fetching */}
            {isSheetLoading && <LogoLoader />}

            {/* Show error if any */}
            {sheetError && (
              <div className="text-red-500 text-sm mt-2">{sheetError}</div>
            )}
          </div>
          {googleSheetColumns.length > 0 && (
            <div className="rounded-lg overflow-hidden mt-6">
              <TableAdv
                tableId={"campaigns_addnewcampaign_leads"}
                columns={[
                  {
                    key: "columnName",
                    label: "Column Name",
                    render: (row) => <span>{row.columnName}</span>,
                  },
                  {
                    key: "selectType",
                    label: "Select Type",
                    render: (row) => (
                      <div className="min-w-[200px]">
                        {isSheetLoading ? (
                          <LogoLoader />
                        ) : (
                          <FormFieldSet
                            type="select"
                            placeholder="Select Type"
                            options={[
                              { label: "Email", value: "email" },
                              { label: "First Name", value: "first_name" },
                              { label: "Last Name", value: "last_name" },
                              { label: "Company Name", value: "company_name" },
                              { label: "Job Title", value: "jobTitle" },
                              { label: "Job Location", value: "jobLocation" },
                              { label: "Source", value: "source" },
                              { label: "Custome Filed", value: "Customefiled" },
                              {
                                label: "personalization",
                                value: "personalization",
                              },
                              { label: "Phone", value: "phone" },
                              { label: "Website", value: "website" },
                            ]}
                            value={row.selectedType}
                            onChange={(val) => {
                              setGoogleSheetColumns((prev) =>
                                prev.map((col) =>
                                  col.columnName === row.columnName
                                    ? { ...col, selectedType: val }
                                    : col,
                                ),
                              );
                            }}
                          />
                        )}
                      </div>
                    ),
                  },
                  {
                    key: "samples",
                    label: "Samples",
                    render: (row) => (
                      <div className="flex flex-col gap-1">
                        {row.samples.map((sample, i) => (
                          <span key={i} className="text-blue-600 text-sm">
                            {sample}
                          </span>
                        ))}
                      </div>
                    ),
                  },
                ]}
                rows={mapTableRows(googleSheetColumns, [
                  { key: "columnName", label: "Column Name" },
                  { key: "selectType", label: "Select Type" },
                  { key: "samples", label: "Samples" },
                ])}
                hideToolBar
              />
            </div>
          )}
        </div>
      )}

      {showUploadModal && (
        <Modal
          title="Upload any one file either excel or csv ?"
          isOpen={showUploadModal} // same state
          onClose={() => setShowUploadModal(false)}
          width="max-w-2xl"
        >
          <div className="p-4">
            <input
              type="file"
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) setUploadedFile(file);
              }}
              className="border border-gray-300 rounded p-2 w-full"
            />
            <div className="flex justify-between mt-4 gap-3">
              <div className="flex gap-3">
                <Button
                  title="Excel"
                  icon={"download"}
                  size="sm"
                  onClick={() => downloadSampleData("excel")}
                />
                <Button
                  title="Csv"
                  icon={"download"}
                  size="sm"
                  onClick={() => downloadSampleData("csv")}
                />
              </div>

              <div className="flex gap-3">
                <Button
                  title="Close"
                  size="sm"
                  onClick={() => setShowUploadModal(false)}
                />
                <Button
                  title="Save"
                  size="sm"
                  onClick={() => {
                    if (!uploadedFile)
                      return alert("Please select a file first");
                    const extension = uploadedFile.name
                      .split(".")
                      .pop()
                      .toLowerCase();
                    const processEmails = (emails) => {
                      const newEmails = emails
                        .filter((email) => !!email) // remove empty/null
                        .map((email) => {
                          const domain =
                            email.split("@")[1]?.split(".")[0] || "";
                          const provider =
                            domain.charAt(0).toUpperCase() +
                            domain.slice(1).toLowerCase();
                          return {
                            email,
                            emailProvider: provider,
                            status: "Not yet Contacted",
                          };
                        });

                      // Remove duplicates
                      setTypedEmails((prev) => {
                        const existingEmails = new Set(
                          prev.map((e) => e.email),
                        );
                        const filteredNew = newEmails.filter(
                          (e) => !existingEmails.has(e.email),
                        );
                        return [...prev, ...filteredNew];
                      });
                      setShowUploadModal(false);
                      setUploadedFile(null);
                    };

                    if (extension === "csv") {
                      Papa.parse(uploadedFile, {
                        header: true,
                        skipEmptyLines: true,
                        complete: (results) => {
                          const emails = results.data.map(
                            (row) => row.email?.trim() || row.Email?.trim(),
                          );
                          processEmails(emails);
                        },
                        error: (err) => console.error("CSV parse error:", err),
                      });
                    } else if (extension === "xls" || extension === "xlsx") {
                      const reader = new FileReader();
                      reader.onload = (evt) => {
                        const data = new Uint8Array(evt.target.result);
                        const workbook = XLSX.read(data, { type: "array" });
                        const firstSheetName = workbook.SheetNames[0];
                        const worksheet = workbook.Sheets[firstSheetName];
                        const jsonData = XLSX.utils.sheet_to_json(worksheet);
                        const emails = jsonData.map(
                          (row) => row.email?.trim() || row.Email?.trim(),
                        );
                        processEmails(emails);
                      };
                      reader.readAsArrayBuffer(uploadedFile);
                    } else {
                      alert("Please upload a CSV or Excel file");
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Leads;
